import { useState } from "react";
import {
  Tesoreria,
  Caso,
  Holding,
  Usuario,
  EventoEstado,
  Documento,
  EstadoTesoreria,
} from "../../types";
import { Badge, getEstadoTesoreriaBadgeVariant } from "../common/Badge";

interface TesoreriaDetailProps {
  tesoreria: Tesoreria;
  caso?: Caso;
  holding?: Holding;
  usuarios: Usuario[];
  eventosEstado: EventoEstado[];
  documentos: Documento[];
  usuarioActual: Usuario;
  onTesoreriaUpdate: (tesoreria: Tesoreria) => void;
  onEventoEstadoCreate: (data: Omit<EventoEstado, "id">) => number;
  onDocumentoCreate: (data: Omit<Documento, "id" | "fechaSubida">) => void;
}

export function TesoreriaDetail({
  tesoreria,
  caso,
  holding,
  usuarios,
  eventosEstado,
  usuarioActual,
  onTesoreriaUpdate,
  onEventoEstadoCreate,
}: TesoreriaDetailProps) {
  const [activeTab, setActiveTab] = useState<"info" | "progreso">("info");
  const [showCambioEstado, setShowCambioEstado] = useState(false);
  const [comentarioCambio, setComentarioCambio] = useState("");
  const [nuevoEstado, setNuevoEstado] = useState<EstadoTesoreria | "">("");

  const isResolved = tesoreria.estado === "pago_recibido";

  const formatMoney = (value?: number) =>
    value ? `$${value.toLocaleString("es-CL")}` : "-";

  // Check if overdue (6+ months)
  const created = new Date(tesoreria.fechaCreacion);
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const isOverdue = tesoreria.estado === "esperando_pago" && created < sixMonthsAgo;

  const getNextStates = (): EstadoTesoreria[] => {
    if (isResolved) return [];
    switch (tesoreria.estado) {
      case "esperando_pago":
        return ["pago_recibido", "vencido"];
      case "vencido":
        return ["en_gestion", "pago_recibido"];
      case "en_gestion":
        return ["pago_recibido", "vencido"];
      default:
        return [];
    }
  };

  const handleCambioEstado = () => {
    if (!nuevoEstado || !comentarioCambio.trim()) return;
    const now = new Date().toISOString().split("T")[0];

    onEventoEstadoCreate({
      entidadTipo: "tesoreria",
      entidadId: tesoreria.id,
      estadoAnterior: tesoreria.estado,
      estadoNuevo: nuevoEstado,
      comentario: comentarioCambio.trim(),
      usuarioId: usuarioActual.id,
      fecha: now,
    });

    onTesoreriaUpdate({
      ...tesoreria,
      estado: nuevoEstado,
      fechaActualizacion: now,
      fechaPago: nuevoEstado === "pago_recibido" ? now : tesoreria.fechaPago,
    });

    setShowCambioEstado(false);
    setComentarioCambio("");
    setNuevoEstado("");
  };

  const getUsuarioNombre = (id?: number) =>
    id ? usuarios.find((u) => u.id === id)?.nombre || "-" : "-";

  const ESTADO_LABELS: Record<string, string> = {
    esperando_pago: "Esperando Pago SII",
    pago_recibido: "Pago Recibido",
    vencido: "Vencido",
    en_gestion: "En Gestion",
  };

  const tabs = [
    { id: "info" as const, label: "Info" },
    { id: "progreso" as const, label: "Progreso" },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-slate-800">
            Tesoreria: {caso?.titulo || `#${tesoreria.solicitudId}`}
          </h2>
          <Badge variant={getEstadoTesoreriaBadgeVariant(tesoreria.estado)}>
            {ESTADO_LABELS[tesoreria.estado] || tesoreria.estado}
          </Badge>
        </div>

        {isOverdue && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            Este pago lleva mas de 6 meses en espera. Considere gestionar con el SII.
          </div>
        )}

        {/* Action buttons */}
        {!isResolved && (
          <div className="flex gap-2 mt-3">
            {getNextStates().map((estado) => (
              <button
                key={estado}
                onClick={() => {
                  setNuevoEstado(estado);
                  setShowCambioEstado(true);
                }}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  estado === "pago_recibido"
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : estado === "vencido"
                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {ESTADO_LABELS[estado]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cambio estado modal */}
      {showCambioEstado && (
        <div className="p-4 bg-yellow-50 border-b border-yellow-200">
          <p className="text-sm font-medium text-yellow-800 mb-2">
            Cambiar estado a: <strong>{ESTADO_LABELS[nuevoEstado || ""] || nuevoEstado}</strong>
          </p>
          <textarea
            value={comentarioCambio}
            onChange={(e) => setComentarioCambio(e.target.value)}
            placeholder="Comentario obligatorio..."
            rows={2}
            className="w-full px-3 py-1.5 border border-yellow-300 rounded-lg text-sm mb-2"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowCambioEstado(false)}
              className="text-xs px-3 py-1.5 text-slate-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleCambioEstado}
              disabled={!comentarioCambio.trim()}
              className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg disabled:bg-slate-300"
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white px-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${
              activeTab === tab.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === "info" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Detalle</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Solicitud:</span>
                  <span className="ml-2 text-slate-800">{caso?.titulo || "-"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Holding:</span>
                  <span className="ml-2 text-slate-800">{holding?.nombre || "-"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Monto Esperado:</span>
                  <span className="ml-2 font-medium text-slate-800">{formatMoney(tesoreria.montoPago)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Fecha Creacion:</span>
                  <span className="ml-2 text-slate-800">
                    {new Date(tesoreria.fechaCreacion).toLocaleDateString("es-CL")}
                  </span>
                </div>
                {tesoreria.fechaPago && (
                  <div>
                    <span className="text-slate-500">Fecha Pago:</span>
                    <span className="ml-2 text-slate-800">
                      {new Date(tesoreria.fechaPago).toLocaleDateString("es-CL")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {tesoreria.observaciones && (
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Observaciones</h3>
                <p className="text-sm text-slate-600">{tesoreria.observaciones}</p>
              </div>
            )}

            {tesoreria.estado === "pago_recibido" && (
              <div className="p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                Pago recibido el {tesoreria.fechaPago ? new Date(tesoreria.fechaPago).toLocaleDateString("es-CL") : "-"}
              </div>
            )}
          </div>
        )}

        {activeTab === "progreso" && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Timeline de Eventos</h3>
            {eventosEstado.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No hay eventos registrados</p>
            ) : (
              <div className="space-y-2">
                {[...eventosEstado]
                  .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
                  .map((evento) => (
                    <div key={evento.id} className="bg-white rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={getEstadoTesoreriaBadgeVariant(evento.estadoNuevo)}>
                          {evento.estadoNuevo.replace("_", " ")}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          desde {evento.estadoAnterior.replace("_", " ")}
                        </span>
                        <span className="text-xs text-slate-400 ml-auto">
                          {new Date(evento.fecha).toLocaleDateString("es-CL")}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{evento.comentario}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Por {getUsuarioNombre(evento.usuarioId)}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
